import type {
  CreateDonationDafRecommendationInput,
  HostedPaymentSessionInput,
  HostedPortalSessionInput,
  PaymentModuleId,
} from '@triedge/domain-core';
import type { PaymentsClient, PaymentsTransport } from './types';

const readText = (value: unknown): string => String(value ?? '').trim();

export const createPaymentsClient = (transport: PaymentsTransport): PaymentsClient => ({
  async ensureStripeCustomerForModule(moduleId: PaymentModuleId): Promise<string> {
    const response = await transport.mutate('ensureStripeCustomer', {
      input: { moduleId },
    }) as { customerId?: unknown };
    const customerId = readText(response?.customerId);
    if (!customerId) {
      throw new Error('Stripe customer sync did not return a customer ID.');
    }
    return customerId;
  },

  async createHostedPaymentSession(input: HostedPaymentSessionInput): Promise<string> {
    const response = await transport.mutate('createHostedPaymentSession', {
      input,
    }) as { url?: unknown };
    const url = readText(response?.url);
    if (!url) {
      throw new Error('Stripe checkout did not return a hosted URL.');
    }
    return url;
  },

  async createHostedPortalSession(input: HostedPortalSessionInput): Promise<string> {
    const response = await transport.mutate('createHostedPortalSession', {
      input,
    }) as { url?: unknown };
    const url = readText(response?.url);
    if (!url) {
      throw new Error('Stripe billing portal did not return a hosted URL.');
    }
    return url;
  },

  async createDonationDafRecommendation(
    input: CreateDonationDafRecommendationInput,
  ) {
    const response = await transport.mutate('createDonationDafRecommendation', {
      input,
    }) as {
      donationId?: unknown;
      status?: unknown;
      provider?: unknown;
      fundingMethod?: unknown;
      providerTransactionRef?: unknown;
      providerResponseResult?: unknown;
      providerMessage?: unknown;
    };
    const donationId = readText(response?.donationId);
    const providerTransactionRef = readText(response?.providerTransactionRef);
    if (!donationId || !providerTransactionRef) {
      throw new Error('Sola DAF recommendation did not return the expected transaction references.');
    }
    return {
      donationId,
      status: readText(response?.status),
      provider: readText(response?.provider) as 'sola',
      fundingMethod: readText(response?.fundingMethod) as 'sola_daf',
      providerTransactionRef,
      providerResponseResult: readText(response?.providerResponseResult) || null,
      providerMessage: readText(response?.providerMessage) || null,
    };
  },
});
